/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Environment } from '../../types';
import { useContext } from 'react';
import { AccessContext } from '../DecisionViewer';
import { Link, Text } from '@cimpress-ui/react';

const genericNoOptionsWrapperStyles = css`
  h3 {
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 1.6rem;
  }

  .halfWeight > {
    h3 {
      font-size: 1.2rem;
      margin: 0 0 1rem;
    }

    p:last-of-type {
      margin: 1rem 0 0;
    }

    div {
      p {
        margin: 1rem 0;
      }

      p:last-of-type {
        margin: 1rem 0 0;
      }

      ul {
        margin-top: 1rem;
      }
    }
  }

  p {
    margin: 1.6rem 0;
  }

  p:last-of-type {
    margin: 1.6rem 0 0;
  }

  ul {
    margin-top: 1.6rem;
  }
`;

const genericNoOptionsListStyles = css`
  margin-left: 3.2rem;
`;

const getActionUrls = ({
  environment,
  orderedSkuCode,
}: {
  environment: Environment;
  orderedSkuCode: string | undefined;
}) => {
  if (!orderedSkuCode) {
    return undefined;
  }

  return {
    fcs:
      environment === 'prd'
        ? 'https://fulfillmentconfigurations.cimpress.io/'
        : 'https://int.fulfillmentconfigurations.cimpress.io/',
    equivalency: `https://productmanager-v2.products.cimpress.io/equivalency-redirect`,
    productManager: `https://productmanager-v2.products.cimpress.io/product-details/${orderedSkuCode}`,
  };
};

export const GenericNoOptions = ({
  orderedSkuCode,
  halfWeight,
}: {
  orderedSkuCode: string | undefined;
  halfWeight?: boolean;
}) => {
  const { environment } = useContext(AccessContext);

  const actionUrls = getActionUrls({ environment, orderedSkuCode });

  return (
    <div css={genericNoOptionsWrapperStyles} data-testid="generic-no-options">
      <div className={halfWeight ? 'halfWeight' : undefined}>
        <Text as="h3">No Options Available</Text>
        {actionUrls ? (
          <div data-testid="action-urls">
            <Text as="p">
              There are no options available for routing this item. Please setup options through one of the following
              approaches:
            </Text>
            <ul css={genericNoOptionsListStyles}>
              <li>
                <Link target="_blank" rel="noreferrer" href={actionUrls.equivalency}>
                  Add Product Equivalency (Legacy)
                </Link>
              </li>
              <li>
                <Link target="_blank" rel="noreferrer" href={actionUrls.fcs}>
                  Associate this product to a Fulfillment Configuration
                </Link>
              </li>
              <li>
                <Link target="_blank" rel="noreferrer" href={actionUrls.productManager}>
                  Add a Fulfiller to the Fulfilled Product (Legacy)
                </Link>
              </li>
            </ul>
          </div>
        ) : (
          <Text as="p">There are no options available for routing this item.</Text>
        )}
        <Text as="p">
          For help configuring options or if you still see this message after configuring options, please contact the
          <Link href="https://cimpress.slack.com/archives/C5XG09F6J" target="_blank" rel="noreferrer">
            #mcp-support-pim
          </Link>{' '}
          channel on Slack.
        </Text>
      </div>
    </div>
  );
};
